import projectThumbnailSrc from '@assets/images/project-thumbnail.png';
import StaticMap, { StaticMapRequest } from '@mapbox/mapbox-sdk/services/static';
import bbox from '@turf/bbox';
import { feature, featureCollection } from '@turf/helpers';
import { centerOfMass, simplify } from '@turf/turf';
import { cn, Image, ImageProps, Popover, PopoverContent, PopoverTrigger, RiInformationFill } from 'component-library';
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { LngLatLike } from 'mapbox-gl';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DraftPlot, Plot, PlotStatusEnum, PlotType } from '@/api/rest/resources/types/plot';
import { MAPBOX_NO_LABELS } from '@/config/constants';
import { rgbaToHex } from '@/utils/colors';
import { getColorOfPlotType } from '@/utils/plot';

const staticClient = StaticMap({
  accessToken: MAPBOX_NO_LABELS.token,
});

const THUMBNAIL_CLASSNAME = 'relative flex-shrink-0';
const THUMBNAIL_IMAGE_CLASSNAME = 'bg-secondary-30';

export type AgricultureProjectThumbnailProps = Omit<ImageProps, 'src'> & {
  plots: Pick<Plot, 'multi_polygon' | 'type'>[];
};

export const AgricultureProjectThumbnail: FC<AgricultureProjectThumbnailProps> = ({ plots, ...delegated }) => {
  const staticMap = useMemo(() => {
    const collection = featureCollection(plots.map((plot) => feature(plot.multi_polygon)));
    const boundingBox = bbox(collection);

    const staticMapRequest = staticClient.getStaticImage({
      ...defaultMapStyles,
      position: {
        // @ts-ignore library typings seem to be incorrect
        bbox: boundingBox,
      },
    });

    return staticMapRequest.url();
  }, [plots]);

  return <ProjectThumbnail src={staticMap} {...delegated} />;
};

export type ConservationProjectThumbnailProps = Omit<ImageProps, 'src'> & { polygons: FeatureCollection };

export const ConservationProjectThumbnail: FC<ConservationProjectThumbnailProps> = ({ polygons, ...delegated }) => {
  const staticMap = useMemo(() => {
    const boundingBox = bbox(polygons);

    const staticMapRequest = staticClient.getStaticImage({
      ...defaultMapStyles,
      position: {
        // @ts-ignore library typings seem to be incorrect
        bbox: boundingBox,
      },
    });

    return staticMapRequest.url();
  }, [polygons]);

  return <ProjectThumbnail src={staticMap} {...delegated} />;
};

const ProjectThumbnail = ({ className, ...delegated }: ImageProps) => {
  return (
    <div className={cn(THUMBNAIL_CLASSNAME, className)}>
      <Image
        alt='project-thumbnail'
        fallback={projectThumbnailSrc}
        className={cn(THUMBNAIL_IMAGE_CLASSNAME, className)}
        {...delegated}
      />
      <div className='absolute bottom-0 right-1'>
        <Attribution />
      </div>
    </div>
  );
};

export type PlotThumbnailProps = Omit<ImageProps, 'src'> & {
  plot:
    | (Pick<Plot, 'type'> & Partial<Pick<Plot, 'multi_polygon' | 'multi_polygon'>>)
    | (Pick<DraftPlot, 'land_type'> & Partial<Pick<DraftPlot, 'multi_polygon' | 'multi_polygon'>>);
  withInfo?: boolean;
};

export const PlotThumbnail: FC<PlotThumbnailProps> = ({ plot, className, withInfo = true, ...delegated }) => {
  const staticMap = useMemo(() => {
    const polygonColor = getColorOfPlotType((plot as Plot).type || (plot as DraftPlot).land_type || PlotType.UNDEFINED);
    const polygonColorHex = rgbaToHex(polygonColor);

    if (!plot.multi_polygon) {
      // Do not raise error here: grouped plots do not have a map to display (they lack of multi_polygon)
      return '';
    }

    const geometry = plot.multi_polygon as MultiPolygon;
    // Simplify geometry to reduce URL length. Mapbox Static Images API encodes GeoJSON in the URL,
    // and multi polygons with hundreds of coordinates can exceed HTTP/2 frame size limits (~8KB).
    const simplifiedFeature = simplify(feature(geometry) as Feature<Polygon | MultiPolygon>, {
      tolerance: 0.0001,
      highQuality: true,
    });

    const isAnalysed = (`status` in plot && plot.status === PlotStatusEnum.analysed) ?? false;
    const staticMapRequest = staticClient.getStaticImage({
      ...defaultMapStyles,
      overlays: [
        {
          geoJson: {
            type: 'Feature',
            geometry: simplifiedFeature.geometry,
            properties: {
              fill: polygonColorHex,
              stroke: polygonColorHex,
              'fill-opacity': isAnalysed ? 1 : 0.4,
            },
          },
        },
      ],
    });

    return staticMapRequest.url();
  }, [plot]);

  return (
    <div className={cn(THUMBNAIL_CLASSNAME, className)}>
      <Image
        alt='plot-thumbnail'
        src={staticMap}
        fallback={projectThumbnailSrc}
        className={cn(THUMBNAIL_IMAGE_CLASSNAME, className)}
        {...delegated}
      />
      {withInfo && (
        <div className='absolute bottom-0 right-1'>
          <Attribution />
        </div>
      )}
    </div>
  );
};

const Attribution = () => {
  const { t } = useTranslation();

  return (
    <Popover>
      <PopoverTrigger onClick={(e) => e.stopPropagation()} data-testid='attribution-button'>
        <RiInformationFill className='fill-white-100/80' />
      </PopoverTrigger>

      <PopoverContent size='small' className='typography-caption' data-testid='attribution-content'>
        <span>
          ©{' '}
          <a
            href='https://www.mapbox.com/about/maps/'
            className='hover:underline active:underline'
            target='_blank'
            rel='noreferrer'
          >
            Mapbox
          </a>
        </span>
        <span>
          ©{' '}
          <a
            href='http://www.openstreetmap.org/copyright'
            className='hover:underline active:underline'
            target='_blank'
            rel='noreferrer'
          >
            OpenStreetMap
          </a>
        </span>
        <a
          href='https://www.mapbox.com/map-feedback/'
          className='hover:underline active:underline'
          target='_blank'
          rel='noreferrer'
        >
          {t('global.misc.improveThisMap')}
        </a>
        <a href='https://maxar.com' className='hover:underline active:underline' target='_blank' rel='noreferrer'>
          Maxar
        </a>
      </PopoverContent>
    </Popover>
  );
};

type PlotMarkerThumbnailProps = {
  plot: Plot;
  className?: string;
  withInfo?: boolean;
};

export const PlotMarkerThumbnail: FC<PlotMarkerThumbnailProps> = ({
  plot,
  className,
  withInfo = true,
  ...delegated
}) => {
  const staticMap = useMemo(() => {
    const centerPoint = centerOfMass(plot.multi_polygon);

    const staticMapRequest = staticClient.getStaticImage({
      ...defaultMapStyles,
      overlays: [
        {
          marker: {
            coordinates: centerPoint.geometry.coordinates as LngLatLike,
            color: 'FFFFFF',
            size: 'large',
          },
        },
      ],
    });

    return staticMapRequest.url();
  }, [plot]);

  return (
    <div className={cn(THUMBNAIL_CLASSNAME, className)}>
      <Image
        alt='plot-marker-thumbnail'
        src={staticMap}
        fallback={projectThumbnailSrc}
        className={cn(THUMBNAIL_IMAGE_CLASSNAME, className)}
        {...delegated}
      />
      {withInfo && (
        <div className='absolute bottom-0 right-1'>
          <Attribution />
        </div>
      )}
    </div>
  );
};

const defaultMapStyles: StaticMapRequest = {
  height: 128,
  width: 128,
  ownerId: MAPBOX_NO_LABELS.ownerId,
  styleId: MAPBOX_NO_LABELS.styleId,
  highRes: true,
  position: 'auto',
  attribution: false,
  logo: false,
  padding: '16',
};
